import React, { useState, useEffect } from 'react'
import {
  PlusIcon,
  FileTextIcon,
  DollarSignIcon,
  CalendarIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  Loader2Icon,
  TrashIcon,
  CreditCardIcon,
  SendIcon,
  DownloadIcon,
  MailIcon,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ActivityTypes, logActivity, logActivityOnLinkedParents } from '../utils/activityLogger'
import { createMilestoneInvoice } from '../lib/createMilestoneInvoice'
import { fetchContractMilestones } from '../lib/milestoneBilling'
import { fetchAlreadyInvoicedJobIds, fetchReadyToBillItems } from '../lib/readyToBill'
import {
  getInvoiceDisplayStatus,
  getInvoiceStatusColor,
  getInvoiceStatusLabel,
} from '../lib/invoiceStatus'
import { downloadInvoicePdf, buildInvoicePdfBlob } from '../lib/downloadInvoicePdf'
import { sendEmail, blobToBase64 } from '../lib/sendEmail'
import { getContactDisplayName } from '../lib/leadHelpers'
import type { CustomerContact } from '../components/customer/types'
import type { BillingMilestone } from '../types/agreement'
import {
  MobilePage,
  MobilePageHeader,
  MobileListRow,
  MobileRowAction,
  MobileEmptyState,
  MobileSection,
  mobile,
} from '../components/mobile'

interface Customer {
  id: string
  company_name?: string
}

interface Job {
  id: string
  job_number: string
  title: string
  scheduled_date?: string
  actual_end_time?: string
  quoted_amount?: number
  actual_amount?: number
  customer_id: string
  property_id: string
  operational_division_id?: string | null
  property?: {
    name: string
    address: string
  }
}

interface InvoiceLineItem {
  id?: string
  description: string
  quantity: number
  unit_price: number
  total: number
}

interface InvoiceRecord {
  id: string
  invoice_number: string
  customer_id: string
  total: number
  amount_paid?: number
  balance_due?: number
  status: string
  invoice_date: string
  due_date: string
  sent_at?: string | null
  notes?: string
  customer?: {
    company_name?: string
    email?: string
    contacts?: CustomerContact[] | null
  }
  line_items?: InvoiceLineItem[]
}

export function InvoiceGeneration() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const customerParam = searchParams.get('customer')
  const invoiceParam = searchParams.get('invoice')

  const [customers, setCustomers] = useState<Customer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<string>(customerParam || '')
  const [periodStart, setPeriodStart] = useState<string>('')
  const [periodEnd, setPeriodEnd] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [completedJobs, setCompletedJobs] = useState<Job[]>([])
  const [generating, setGenerating] = useState<boolean>(false)
  const [recentInvoices, setRecentInvoices] = useState<InvoiceRecord[]>([])
  const [creatingInvoice, setCreatingInvoice] = useState(false)
  const [invoiceCreated, setInvoiceCreated] = useState(false)
  const [progressContracts, setProgressContracts] = useState<any[]>([])
  const [selectedProgressContract, setSelectedProgressContract] = useState('')
  const [contractMilestones, setContractMilestones] = useState<BillingMilestone[]>([])
  const [selectedMilestoneId, setSelectedMilestoneId] = useState('')
  const [creatingMilestoneInvoice, setCreatingMilestoneInvoice] = useState(false)

  // Invoice view state
  const [viewingInvoice, setViewingInvoice] = useState<InvoiceRecord | null>(null)
  const [viewLineItems, setViewLineItems] = useState<InvoiceLineItem[]>([])
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [sending, setSending] = useState(false)
  const [emailing, setEmailing] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [emailModalMode, setEmailModalMode] = useState<'send' | 'email'>('email')
  const [emailTo, setEmailTo] = useState('')
  const [emailCcIds, setEmailCcIds] = useState<Set<string>>(new Set())
  const [emailModalTotal, setEmailModalTotal] = useState(0)

  useEffect(() => {
    fetchCustomers()
    fetchRecentInvoices()
    if (invoiceParam) {
      loadInvoice(invoiceParam)
    }
  }, [invoiceParam])

  useEffect(() => {
    if (!selectedCustomer) {
      setProgressContracts([])
      setSelectedProgressContract('')
      setContractMilestones([])
      return
    }
    const loadProgressContracts = async () => {
      try {
        const { data, error } = await supabase
          .from('contracts')
          .select('id, contract_number, title, total_amount, contract_total_amount, payment_terms, property_id, operational_division_id')
          .eq('customer_id', selectedCustomer)
          .eq('billing_frequency', 'progress')
          .in('status', ['active', 'pending', 'draft'])

        if (error) throw error
        setProgressContracts(data || [])
      } catch (err) {
        console.error('Error loading progress contracts:', err)
        setProgressContracts([])
      }
    }
    loadProgressContracts()
  }, [selectedCustomer])

  useEffect(() => {
    if (!selectedProgressContract) {
      setContractMilestones([])
      setSelectedMilestoneId('')
      return
    }
    fetchContractMilestones(selectedProgressContract)
      .then((milestones) => {
        setContractMilestones(milestones)
        const firstPending = milestones.find((m) => m.status === 'pending')
        setSelectedMilestoneId(firstPending?.id || '')
      })
      .catch(() => {
        setContractMilestones([])
        setSelectedMilestoneId('')
      })
  }, [selectedProgressContract])

  const loadInvoice = async (invoiceId: string) => {
    try {
      setLoadingInvoice(true)
      const { data: inv, error } = await supabase
        .from('invoices')
        .select('*, customer:customers(company_name, email, contacts)')
        .eq('id', invoiceId)
        .single()

      if (error) throw error
      if (!inv) throw new Error('Invoice not found')

      const total = Number(inv.total || inv.total_amount || inv.subtotal || 0)
      const amountPaid = Number(inv.amount_paid ?? 0)
      const balanceDue = Number(inv.balance_due ?? (total - amountPaid))
      setViewingInvoice({
        ...inv,
        total,
        amount_paid: amountPaid,
        balance_due: balanceDue,
        status: getInvoiceDisplayStatus({ status: inv.status, balance_due: balanceDue, due_date: inv.due_date }),
        invoice_date: inv.invoice_date || inv.issue_date || inv.created_at,
      })

      // Fetch line items - try both table names for compatibility
      let lineItems: any[] = []
      const { data: items1, error: err1 } = await supabase
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('display_order', { ascending: true })

      if (!err1 && items1 && items1.length > 0) {
        lineItems = items1
      } else {
        const { data: items2 } = await supabase
          .from('invoice_line_items')
          .select('*')
          .eq('invoice_id', invoiceId)
        if (items2) lineItems = items2
      }

      setViewLineItems(
        lineItems.map((item: any) => ({
          id: item.id,
          description: item.description || '',
          quantity: Number(item.quantity || 1),
          unit_price: Number(item.unit_price || 0),
          total: Number(item.total || item.unit_price || 0),
        }))
      )
    } catch (err: any) {
      console.error('Error loading invoice:', err)
      alert('Could not load invoice: ' + (err.message || 'Unknown error'))
    } finally {
      setLoadingInvoice(false)
    }
  }

  const fetchRecentInvoices = async () => {
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*, customer:customers(company_name, email)')
        .order('created_at', { ascending: false })
        .limit(10)

      if (!error && data) {
        setRecentInvoices(
          data.map((inv: any) => {
            const total = Number(inv.total || inv.total_amount || inv.subtotal || 0)
            const amountPaid = Number(inv.amount_paid ?? 0)
            const balanceDue = Number(inv.balance_due ?? (total - amountPaid))
            return {
              ...inv,
              total,
              amount_paid: amountPaid,
              balance_due: balanceDue,
              status: getInvoiceDisplayStatus({ status: inv.status, balance_due: balanceDue, due_date: inv.due_date }),
              invoice_date: inv.invoice_date || inv.issue_date || inv.created_at,
            }
          })
        )
      }
    } catch {
      // Silently fail
    }
  }

  const handleCreateMilestoneInvoice = async () => {
    if (!selectedProgressContract || !selectedMilestoneId) return
    const milestone = contractMilestones.find((m) => m.id === selectedMilestoneId)
    const contract = progressContracts.find((c) => c.id === selectedProgressContract)
    if (!milestone || !contract) return

    try {
      setCreatingMilestoneInvoice(true)
      const result = await createMilestoneInvoice(milestone, {
        id: contract.id,
        customer_id: selectedCustomer,
        property_id: contract.property_id,
        payment_terms: contract.payment_terms,
        title: contract.title,
        operational_division_id: contract.operational_division_id,
      })
      await fetchRecentInvoices()
      const refreshed = await fetchContractMilestones(selectedProgressContract)
      setContractMilestones(refreshed)
      navigate(`/invoice-generation?invoice=${result.invoiceId}`)
    } catch (err: any) {
      alert(err?.message || 'Could not create milestone invoice.')
    } finally {
      setCreatingMilestoneInvoice(false)
    }
  }

  const handleCreateInvoice = async () => {
    if (completedJobs.length === 0) return

    try {
      setCreatingInvoice(true)
      const invoiceNumber = `INV-${new Date().getFullYear()}-${String(Date.now()).slice(-4)}`
      const dueDate = new Date()
      dueDate.setDate(dueDate.getDate() + 30)

      const firstJob = completedJobs[0]
      const propertyId = firstJob?.property_id || null
      const { data: invoice, error: invoiceError } = await supabase
        .from('invoices')
        .insert({
          invoice_number: invoiceNumber,
          customer_id: selectedCustomer,
          property_id: propertyId,
          operational_division_id: firstJob.operational_division_id || null,
          total: totalAmount,
          subtotal: totalAmount,
          status: 'draft',
          invoice_date: new Date().toISOString().split('T')[0],
          due_date: dueDate.toISOString().split('T')[0],
          balance_due: totalAmount,
          notes: `Invoice for ${completedJobs.length} completed job(s)${periodStart ? ` from ${periodStart}` : ''}${periodEnd ? ` to ${periodEnd}` : ''}`,
        })
        .select()
        .single()

      if (invoiceError) throw invoiceError

      if (invoice) {
        const invoiceDescription = `Invoice ${invoiceNumber} created`
        await logActivity({
          activity_type: ActivityTypes.INVOICE_CREATED,
          entity_type: 'customer',
          entity_id: selectedCustomer,
          description: invoiceDescription,
          metadata: {
            child_entity_type: 'invoice',
            child_entity_id: invoice.id,
            invoice_number: invoiceNumber,
            property_id: propertyId,
          },
        })
        await logActivityOnLinkedParents({
          parents: {
            customer_id: selectedCustomer,
            property_id: propertyId,
          },
          activity_type: ActivityTypes.INVOICE_CREATED,
          description: invoiceDescription,
          metadata: {
            child_entity_type: 'invoice',
            child_entity_id: invoice.id,
            invoice_number: invoiceNumber,
          },
        })

        for (let i = 0; i < completedJobs.length; i++) {
          const job = completedJobs[i]
          try {
            await supabase.from('invoice_items').insert({
              invoice_id: invoice.id,
              job_id: job.id,
              display_order: i,
              description: `${job.job_number} - ${job.title}`,
              quantity: 1,
              unit_price: job.actual_amount || job.quoted_amount || 0,
              total: job.actual_amount || job.quoted_amount || 0,
            })
          } catch {
            // Fallback to invoice_line_items if invoice_items doesn't exist
            try {
              await supabase.from('invoice_line_items').insert({
                invoice_id: invoice.id,
                description: `${job.job_number} - ${job.title}`,
                quantity: 1,
                unit_price: job.actual_amount || job.quoted_amount || 0,
                total: job.actual_amount || job.quoted_amount || 0,
              })
            } catch {
              console.warn('Could not insert line item')
            }
          }
        }
      }

      setInvoiceCreated(true)
      await fetchRecentInvoices()
      setTimeout(() => setInvoiceCreated(false), 5000)
    } catch (err: any) {
      console.error('Error creating invoice:', err)
      alert('Failed to create invoice: ' + (err.message || 'Unknown error'))
    } finally {
      setCreatingInvoice(false)
    }
  }

  const fetchCustomers = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('customers')
        .select('id, company_name')
        .order('company_name')

      if (error) throw error
      setCustomers(data || [])
    } catch (error) {
      console.error('Error fetching customers:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleGenerateInvoice = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedCustomer || !periodStart || !periodEnd) {
      alert('Please fill in all fields')
      return
    }

    try {
      setGenerating(true)
      // Same billable gate as Ready to Bill: approved, not already invoiced, not blocked
      // (unapproved hours, ambiguous contract billing, included, etc.).
      const [{ data, error }, alreadyInvoicedJobIds, readyItems] = await Promise.all([
        supabase
          .from('jobs')
          .select('*, property:properties(id, name, address)')
          .eq('customer_id', selectedCustomer)
          .eq('status', 'approved'),
        fetchAlreadyInvoicedJobIds(),
        fetchReadyToBillItems(),
      ])

      if (error) throw error
      const billableJobIds = new Set(
        readyItems
          .filter((i) => i.kind === 'completed_job' && i.job_id && !i.blocked)
          .map((i) => i.job_id as string)
      )
      const jobs = (data || []).filter(
        (j: Job) => !alreadyInvoicedJobIds.has(j.id) && billableJobIds.has(j.id)
      )
      const periodStartDate = new Date(periodStart)
      const periodEndDate = new Date(periodEnd)
      periodEndDate.setHours(23, 59, 59, 999)
      const inRange = jobs.filter((job: Job) => {
        const endTime = job.actual_end_time ? new Date(job.actual_end_time) : null
        const scheduled = job.scheduled_date ? new Date(job.scheduled_date) : null
        const dateToUse = endTime || scheduled
        if (!dateToUse) return false
        return dateToUse >= periodStartDate && dateToUse <= periodEndDate
      })
      setCompletedJobs(inRange)
    } catch (error) {
      console.error('Error fetching completed jobs:', error)
      alert('Error fetching completed jobs')
    } finally {
      setGenerating(false)
    }
  }

  const formatDate = (date: string) => {
    if (!date) return '—'
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatCurrency = (amount?: number) => {
    if (!amount && amount !== 0) return '$0.00'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const totalAmount = completedJobs.reduce((sum, job) => {
    return sum + (job.actual_amount || job.quoted_amount || 0)
  }, 0)

  const handleDeleteInvoice = async (invoiceId: string) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return
    try {
      await supabase.from('invoice_items').delete().eq('invoice_id', invoiceId)
      await supabase.from('invoice_line_items').delete().eq('invoice_id', invoiceId)
      const { error } = await supabase.from('invoices').delete().eq('id', invoiceId)
      if (error) throw error
      await fetchRecentInvoices()
      if (viewingInvoice?.id === invoiceId) {
        setViewingInvoice(null)
        setViewLineItems([])
        navigate('/invoice-generation', { replace: true })
      }
    } catch (err: any) {
      alert('Failed to delete invoice: ' + (err.message || 'Unknown error'))
    }
  }

  const openInvoiceEmailModal = (mode: 'send' | 'email', invTotal: number) => {
    if (!viewingInvoice) return
    setEmailModalMode(mode)
    setEmailModalTotal(invTotal)
    setEmailTo((viewingInvoice.customer?.email || '').trim())
    setEmailCcIds(new Set())
    setEmailModalOpen(true)
  }

  const toggleEmailCc = (contactId: string) => {
    setEmailCcIds((prev) => {
      const next = new Set(prev)
      if (next.has(contactId)) next.delete(contactId)
      else next.add(contactId)
      return next
    })
  }

  const handleConfirmInvoiceEmail = async () => {
    if (!viewingInvoice) return
    const to = emailTo.trim()
    if (!to) {
      alert('Enter a To email address.')
      return
    }
    const contacts = Array.isArray(viewingInvoice.customer?.contacts)
      ? viewingInvoice.customer!.contacts!
      : []
    const cc = contacts
      .filter((c) => emailCcIds.has(c.id) && (c.email || '').trim())
      .map((c) => c.email.trim())
      .filter((em) => em.toLowerCase() !== to.toLowerCase())

    const invTotal = emailModalTotal
    const markSent = emailModalMode === 'send' || viewingInvoice.status === 'draft'
    const setBusy = emailModalMode === 'send' ? setSending : setEmailing

    try {
      setBusy(true)
      const blob = await buildInvoicePdfBlob(buildInvoicePdfProps(invTotal))
      const base64 = await blobToBase64(blob)
      await sendEmail({
        to,
        ...(cc.length ? { cc } : {}),
        subject: `Invoice ${viewingInvoice.invoice_number} from RBC Facility Services`,
        text: `Please find attached invoice ${viewingInvoice.invoice_number} for $${Number(invTotal).toFixed(2)}.`,
        attachments: [
          { filename: `${viewingInvoice.invoice_number}.pdf`, content: base64, contentType: 'application/pdf' },
        ],
      })

      if (markSent) {
        const sentAt = new Date().toISOString()
        const { error } = await supabase
          .from('invoices')
          .update({ status: 'sent', sent_at: sentAt })
          .eq('id', viewingInvoice.id)
        if (error) throw error
        setViewingInvoice({ ...viewingInvoice, status: 'sent', sent_at: sentAt })
      }

      const ccNote = cc.length ? ` (CC: ${cc.join(', ')})` : ''
      await logActivity({
        activity_type: ActivityTypes.INVOICE_UPDATED,
        entity_type: 'customer',
        entity_id: viewingInvoice.customer_id,
        description: `Invoice ${viewingInvoice.invoice_number} emailed to ${to}${ccNote}`,
        metadata: { child_entity_type: 'invoice', child_entity_id: viewingInvoice.id, to, cc },
      })
      await fetchRecentInvoices()
      setEmailModalOpen(false)
      if (emailModalMode === 'email') {
        alert('Invoice emailed successfully.')
      }
    } catch (err: any) {
      alert('Failed to email invoice: ' + (err.message || 'Unknown error'))
    } finally {
      setBusy(false)
    }
  }

  const handleSendInvoice = async () => {
    const invTotal = viewLineItems.reduce((s, li) => s + li.total, 0) || viewingInvoice?.total || 0
    openInvoiceEmailModal('send', invTotal)
  }

  const buildInvoicePdfProps = (invTotal: number) => ({
    invoiceNumber: viewingInvoice!.invoice_number,
    invoiceDate: viewingInvoice!.invoice_date,
    dueDate: viewingInvoice!.due_date,
    status: getInvoiceStatusLabel(viewingInvoice!.status),
    customerName: viewingInvoice!.customer?.company_name || 'Customer',
    lineItems: viewLineItems.length > 0 ? viewLineItems : [{ description: 'Invoice total', quantity: 1, unit_price: invTotal, total: invTotal }],
    subtotal: invTotal,
    amountPaid: viewingInvoice!.amount_paid ?? 0,
    balanceDue: viewingInvoice!.balance_due ?? invTotal,
    notes: viewingInvoice!.notes,
  })

  const handleDownloadPdf = async (invTotal: number) => {
    if (!viewingInvoice) return
    try {
      setDownloadingPdf(true)
      await downloadInvoicePdf(buildInvoicePdfProps(invTotal), viewingInvoice.invoice_number)
    } catch (err: any) {
      alert('Failed to generate PDF: ' + (err.message || 'Unknown error'))
    } finally {
      setDownloadingPdf(false)
    }
  }

  const handleEmailInvoice = async (invTotal: number) => {
    openInvoiceEmailModal('email', invTotal)
  }

  // Invoice View Mode
  if (loadingInvoice) {
    return (
      <MobilePage className="flex items-center justify-center min-h-[400px]">
        <Loader2Icon className="w-8 h-8 animate-spin text-blue-600" />
      </MobilePage>
    )
  }

  if (viewingInvoice) {
    const invTotal = viewLineItems.reduce((s, li) => s + li.total, 0) || viewingInvoice.total
    return (
      <MobilePage>
        <MobilePageHeader
          title={viewingInvoice.invoice_number || 'Invoice'}
          onBack={() => {
            setViewingInvoice(null)
            setViewLineItems([])
            navigate('/invoice-generation', { replace: true })
          }}
          backLabel="Back to Invoice Generation"
        />
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 -mx-1 px-1">
          {viewingInvoice.status === 'draft' && (
            <button
              type="button"
              onClick={handleSendInvoice}
              disabled={sending}
              className={`${mobile.primaryBtn} flex-shrink-0`}
            >
              {sending ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <SendIcon className="w-4 h-4" />}
              Send
            </button>
          )}
          <button
            type="button"
            onClick={() => handleDownloadPdf(invTotal)}
            disabled={downloadingPdf}
            className={`${mobile.secondaryBtn} flex-shrink-0`}
          >
            {downloadingPdf ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <DownloadIcon className="w-4 h-4" />}
            PDF
          </button>
          <button
            type="button"
            onClick={() => handleEmailInvoice(invTotal)}
            disabled={emailing}
            className={`${mobile.secondaryBtn} flex-shrink-0`}
          >
            {emailing ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <MailIcon className="w-4 h-4" />}
            Email
          </button>
          <button
            type="button"
            onClick={() => navigate(`/payments?invoiceId=${viewingInvoice.id}`)}
            className={`${mobile.successBtn} flex-shrink-0`}
          >
            <CreditCardIcon className="w-4 h-4" />
            Pay
          </button>
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className={`${mobile.secondaryBtn} flex-shrink-0`}
          >
            Billing
          </button>
          <button
            type="button"
            onClick={() => handleDeleteInvoice(viewingInvoice.id)}
            className={`${mobile.dangerBtn} flex-shrink-0`}
          >
            <TrashIcon className="w-4 h-4" />
            Delete
          </button>
        </div>

        <div className="bg-white p-4 sm:p-8 rounded-xl shadow-sm border border-gray-200">
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">INVOICE</h1>
              <p className="text-lg text-gray-600 mt-1">{viewingInvoice.invoice_number}</p>
            </div>
              <div className="text-right">
                <span
                  className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getInvoiceStatusColor(viewingInvoice.status)}`}
                >
                  {getInvoiceStatusLabel(viewingInvoice.status)}
                </span>
                {viewingInvoice.sent_at && (
                  <p className="text-xs text-gray-500 mt-1">Sent {formatDate(viewingInvoice.sent_at)}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Bill To</h3>
                <p className="text-lg font-medium text-gray-900">
                  {viewingInvoice.customer?.company_name || 'Customer'}
                </p>
              </div>
              <div className="text-right">
                <div className="space-y-1">
                  <div>
                    <span className="text-sm text-gray-500">Issue Date: </span>
                    <span className="text-sm font-medium">
                      {formatDate(viewingInvoice.invoice_date)}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Due Date: </span>
                    <span className="text-sm font-medium">
                      {formatDate(viewingInvoice.due_date)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {viewLineItems.length > 0 ? (
              <div className="mb-8">
                <div className="native-mobile-list space-y-2 mb-2">
                  {viewLineItems.map((item, idx) => (
                    <div
                      key={item.id || idx}
                      className="rounded-xl border border-gray-200 p-3"
                    >
                      <p className="text-sm text-gray-900 font-medium">{item.description}</p>
                      <div className="mt-1.5 flex items-center justify-between text-xs text-gray-600">
                        <span>
                          Qty {item.quantity} · {formatCurrency(item.unit_price)}
                        </span>
                        <span className="font-semibold text-gray-900 tabular-nums">
                          {formatCurrency(item.total)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="native-desktop-table">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-3 text-sm font-medium text-gray-600">
                          Description
                        </th>
                        <th className="text-right py-3 text-sm font-medium text-gray-600 w-20">
                          Qty
                        </th>
                        <th className="text-right py-3 text-sm font-medium text-gray-600 w-32">
                          Unit Price
                        </th>
                        <th className="text-right py-3 text-sm font-medium text-gray-600 w-32">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewLineItems.map((item, idx) => (
                        <tr key={item.id || idx} className="border-b border-gray-200">
                          <td className="py-3 text-sm text-gray-900">{item.description}</td>
                          <td className="py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                          <td className="py-3 text-sm text-gray-900 text-right">
                            {formatCurrency(item.unit_price)}
                          </td>
                          <td className="py-3 text-sm text-gray-900 text-right font-medium">
                            {formatCurrency(item.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="mb-8 py-6 text-center text-gray-500 border rounded-lg">
                No line items found for this invoice.
              </div>
            )}

            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(invTotal)}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-gray-600">Tax</span>
                  <span className="font-medium">$0.00</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t-2 border-gray-900 pt-2">
                  <span>Total</span>
                  <span>{formatCurrency(invTotal)}</span>
                </div>
                {(viewingInvoice.amount_paid ?? 0) > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-green-700">
                      <span>Paid</span>
                      <span>{formatCurrency(viewingInvoice.amount_paid ?? 0)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold">
                      <span>Balance due</span>
                      <span>{formatCurrency(viewingInvoice.balance_due ?? 0)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {viewingInvoice.notes && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
                <p className="text-sm text-gray-700">{viewingInvoice.notes}</p>
              </div>
            )}
        </div>

        {emailModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="px-5 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">
                  {emailModalMode === 'send' ? 'Send invoice' : 'Email invoice'}
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Choose the To address and optional CC contacts from this customer.
                </p>
              </div>
              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                  <input
                    type="email"
                    value={emailTo}
                    onChange={(e) => setEmailTo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="billing@company.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Defaults to the company email on the customer.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">CC contacts</label>
                  {(() => {
                    const contacts = (Array.isArray(viewingInvoice.customer?.contacts)
                      ? viewingInvoice.customer!.contacts!
                      : []
                    ).filter((c) => (c.email || '').trim())
                    if (contacts.length === 0) {
                      return (
                        <p className="text-sm text-gray-500">
                          No contact emails on this customer. Add people under Customer Contacts to CC them here.
                        </p>
                      )
                    }
                    return (
                      <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-56 overflow-y-auto">
                        {contacts.map((c) => (
                          <label key={c.id} className="flex items-start gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              className="mt-1"
                              checked={emailCcIds.has(c.id)}
                              onChange={() => toggleEmailCc(c.id)}
                            />
                            <span className="text-sm">
                              <span className="font-medium text-gray-900">{getContactDisplayName(c)}</span>
                              {c.title ? <span className="text-gray-500"> · {c.title}</span> : null}
                              <span className="block text-gray-600">{c.email}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    )
                  })()}
                </div>
              </div>
              <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEmailModalOpen(false)}
                  disabled={sending || emailing}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmInvoiceEmail}
                  disabled={sending || emailing || !emailTo.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-1 disabled:opacity-50"
                >
                  {(sending || emailing) ? <Loader2Icon className="w-4 h-4 animate-spin" /> : <MailIcon className="w-4 h-4" />}
                  {emailModalMode === 'send' ? 'Send' : 'Email'}
                </button>
              </div>
            </div>
          </div>
        )}

      </MobilePage>
    )
  }

  return (
    <MobilePage>
      <MobilePageHeader
        title="Invoice Generation"
        subtitle="Create invoices from completed jobs"
        actions={
          <button type="button" onClick={() => navigate('/billing')} className={mobile.secondaryBtn}>
            All Invoices
          </button>
        }
      />

      <MobileSection title="Generate Invoice">
          <form onSubmit={handleGenerateInvoice} className="space-y-4">
            <div>
              <label className={mobile.label}>Customer</label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                className={mobile.field}
                required
                disabled={loading}
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.company_name || 'Unnamed Customer'}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={mobile.label}>Period Start</label>
                <input
                  type="date"
                  value={periodStart}
                  onChange={(e) => setPeriodStart(e.target.value)}
                  className={mobile.field}
                  required
                />
              </div>
              <div>
                <label className={mobile.label}>Period End</label>
                <input
                  type="date"
                  value={periodEnd}
                  onChange={(e) => setPeriodEnd(e.target.value)}
                  className={mobile.field}
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={generating || loading}
              className={`${mobile.primaryBtn} w-full sm:w-auto`}
            >
              <PlusIcon className="w-5 h-5" />
              {generating ? 'Searching...' : 'Find Completed Jobs'}
            </button>
          </form>
      </MobileSection>

      {progressContracts.length > 0 && (
        <MobileSection
          title="Progress billing"
          description="Bill a pending milestone on a one-time contract with progress billing."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={mobile.label}>Contract</label>
              <select
                value={selectedProgressContract}
                onChange={(e) => setSelectedProgressContract(e.target.value)}
                className={mobile.field}
              >
                <option value="">Select contract...</option>
                {progressContracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.contract_number} — {c.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={mobile.label}>Milestone</label>
              <select
                value={selectedMilestoneId}
                onChange={(e) => setSelectedMilestoneId(e.target.value)}
                className={mobile.field}
                disabled={!selectedProgressContract}
              >
                <option value="">Select milestone...</option>
                {contractMilestones
                  .filter((m) => m.status === 'pending')
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label} — ${Number(m.amount).toFixed(2)} ({m.percentage}%)
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={handleCreateMilestoneInvoice}
            disabled={!selectedMilestoneId || creatingMilestoneInvoice}
            className={`${mobile.primaryBtn} mt-4 w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800`}
          >
            {creatingMilestoneInvoice ? 'Creating...' : 'Create milestone invoice'}
          </button>
        </MobileSection>
      )}

      {completedJobs.length > 0 && (
        <MobileSection title={`Completed Jobs (${completedJobs.length})`}>
            <div className="space-y-2 mb-4">
              {completedJobs.map((job) => (
                <MobileListRow
                  key={job.id}
                  chevron={false}
                  leading={<FileTextIcon className="w-5 h-5 text-blue-600" />}
                  title={`${job.job_number} - ${job.title}`}
                  subtitle={
                    <>
                      {job.property?.name || job.property?.address || 'No property'}
                      {job.actual_end_time && ` · ${formatDate(job.actual_end_time)}`}
                    </>
                  }
                  trailing={
                    <p className="text-sm font-semibold text-gray-900 tabular-nums">
                      {formatCurrency(job.actual_amount || job.quoted_amount)}
                    </p>
                  }
                />
              ))}
            </div>
            <div className="pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <p className="text-base font-semibold text-gray-900">Total Amount</p>
                <p className="text-base font-semibold text-blue-600 tabular-nums">
                  {formatCurrency(totalAmount)}
                </p>
              </div>
              {invoiceCreated ? (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-green-700">
                  <CheckCircleIcon className="w-5 h-5" />
                  Invoice created successfully!
                </div>
              ) : (
                <button
                  onClick={handleCreateInvoice}
                  disabled={creatingInvoice}
                  className={`${mobile.successBtn} w-full`}
                >
                  <DollarSignIcon className="w-5 h-5" />
                  {creatingInvoice
                    ? 'Creating Invoice...'
                    : `Create Invoice for ${formatCurrency(totalAmount)}`}
                </button>
              )}
            </div>
        </MobileSection>
      )}

      {completedJobs.length === 0 &&
        !generating &&
        periodStart &&
        periodEnd &&
        selectedCustomer && (
          <MobileEmptyState
            title="No completed jobs"
            description="No completed jobs found for this customer in the selected period."
          />
        )}

      <MobileSection title="Recent Invoices" flush>
          {recentInvoices.length === 0 ? (
            <p className="text-gray-500 text-center py-6 px-4 text-sm">
              No invoices created yet. Generate an invoice above to get started.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentInvoices.map((invoice) => (
                <MobileListRow
                  key={invoice.id}
                  className="rounded-none border-0 shadow-none"
                  leading={<FileTextIcon className="w-5 h-5 text-blue-600" />}
                  title={invoice.invoice_number}
                  subtitle={invoice.customer?.company_name || 'Unknown'}
                  badges={
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs rounded-full ${getInvoiceStatusColor(invoice.status)}`}
                    >
                      {getInvoiceStatusLabel(invoice.status)}
                    </span>
                  }
                  meta={
                    <span className="tabular-nums">
                      {formatCurrency(invoice.total)} · {formatDate(invoice.invoice_date)}
                    </span>
                  }
                  onClick={() => {
                    navigate(`/invoice-generation?invoice=${invoice.id}`)
                    loadInvoice(invoice.id)
                  }}
                  footer={
                    <MobileRowAction
                      tone="danger"
                      onClick={() => handleDeleteInvoice(invoice.id)}
                    >
                      <TrashIcon className="w-4 h-4" />
                      Delete
                    </MobileRowAction>
                  }
                />
              ))}
            </div>
          )}
      </MobileSection>
    </MobilePage>
  )
}
